/**
 * Third intro screen — choose between starting fresh or loading a save.
 *
 * "Load Game" opens a slot panel where the player can pick a `.txt`
 * file previously downloaded from the pause menu. The chosen file is
 * read + handed to the caller as a raw string (parsing happens inside
 * `SaveGameService`, not here, so file errors stay close to the schema
 * logic).
 */

export interface LoadGameCallbacks {
  readonly onNewGame: () => void;
  /** Fires when the user picks a save file. The string is the raw
   *  file contents — pass it to `SaveGameService.parse()`. */
  readonly onLoadFile: (raw: string) => Promise<void> | void;
}

export class LoadGameOverlay {
  readonly element: HTMLElement;
  private readonly cards: HTMLElement;
  private readonly loadPanel: HTMLElement;
  private readonly loadStatusEl: HTMLElement;
  private readonly fileInput: HTMLInputElement;
  private busy = false;

  constructor(private readonly callbacks: LoadGameCallbacks) {
    this.element = document.createElement('div');
    this.element.className = 'intro-overlay load-game-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'intro-backdrop';

    const stars = document.createElement('div');
    stars.className = 'intro-stars';
    for (let i = 0; i < 60; i++) {
      const star = document.createElement('span');
      star.className = 'intro-star';
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.opacity = `${(0.25 + Math.random() * 0.7).toFixed(2)}`;
      star.style.animationDelay = `${(Math.random() * -10).toFixed(2)}s`;
      star.style.animationDuration = `${(3 + Math.random() * 6).toFixed(2)}s`;
      stars.appendChild(star);
    }

    const root = document.createElement('div');
    root.className = 'intro-root';

    const header = document.createElement('header');
    header.className = 'intro-header';
    header.innerHTML = `
      <div class="intro-eyebrow">PIRATE COMMAND</div>
      <h1 class="intro-title">Continue or begin anew</h1>
      <p class="intro-subtitle">Pick up where you left off, or carve a fresh wake through the void.</p>
    `;

    this.cards = document.createElement('div');
    this.cards.className = 'intro-cards';

    this.cards.appendChild(this.buildCard({
      title: 'New Game',
      tagline: 'Forge a new pirate',
      blurb:
        'Start clean. Customise your ship, claim your call sign, and chart your own raid log from the first jump.',
      icon: NEW_GAME_ICON,
      cta: 'Begin →',
      onClick: () => this.callbacks.onNewGame()
    }));

    this.cards.appendChild(this.buildCard({
      title: 'Load Game',
      tagline: 'Resume a campaign',
      blurb:
        'Restore a previous voyage from a save file — your ship, your faction standing, your claims.',
      icon: LOAD_GAME_ICON,
      cta: 'Browse saves →',
      onClick: () => this.openLoadPanel()
    }));

    // Hidden file input — clicked programmatically when the player
    // picks the slot. Accepts both .txt (default) and .json so saves
    // dragged out of other tools still load.
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = '.txt,.json,text/plain,application/json';
    this.fileInput.style.display = 'none';
    this.fileInput.addEventListener('change', () => this.handleFileChange());

    this.loadPanel = document.createElement('div');
    this.loadPanel.className = 'load-panel';
    this.loadPanel.innerHTML = `
      <div class="load-panel-head">
        <h3>Saved campaigns</h3>
        <button class="load-panel-close" type="button" aria-label="Back">←</button>
      </div>
      <div class="load-panel-list">
        <button class="load-slot" type="button" data-action="pick-file">
          <div class="load-slot-icon">${SAVE_FILE_ICON}</div>
          <div class="load-slot-body">
            <div class="load-slot-title">Load save file</div>
            <div class="load-slot-meta">Pick a .txt save downloaded from the pause menu.</div>
          </div>
          <div class="load-slot-stub">Browse</div>
        </button>
      </div>
      <div class="load-panel-status" role="status" aria-live="polite"></div>
      <div class="load-panel-footer">Saves are plain JSON wrapped in a .txt file.</div>
    `;
    const closeBtn = this.loadPanel.querySelector(
      '.load-panel-close'
    ) as HTMLButtonElement;
    closeBtn.addEventListener('click', () => this.closeLoadPanel());
    const slotBtn = this.loadPanel.querySelector(
      '[data-action="pick-file"]'
    ) as HTMLButtonElement;
    slotBtn.addEventListener('click', () => this.fileInput.click());
    this.loadStatusEl = this.loadPanel.querySelector(
      '.load-panel-status'
    ) as HTMLElement;

    const footer = document.createElement('footer');
    footer.className = 'intro-footer';
    footer.textContent = 'v0.5.0 · Pirate Cut';

    root.append(header, this.cards, this.loadPanel, footer);
    this.element.append(backdrop, stars, root, this.fileInput);
  }

  show(): void {
    requestAnimationFrame(() => {
      this.element.classList.add('is-visible');
    });
  }

  async hideAndDispose(): Promise<void> {
    this.element.classList.remove('is-visible');
    await new Promise((resolve) => setTimeout(resolve, 320));
    this.element.remove();
  }

  /** Surface an error inside the load panel — used by the FactionHub
   *  when `SaveGameService.parse()` rejects the picked file. */
  setError(message: string): void {
    this.busy = false;
    this.loadStatusEl.textContent = message;
    this.loadStatusEl.classList.add('is-error');
  }

  private openLoadPanel(): void {
    this.element.classList.add('is-loading-panel');
    this.loadStatusEl.textContent = '';
    this.loadStatusEl.classList.remove('is-error');
  }

  private closeLoadPanel(): void {
    if (this.busy) return;
    this.element.classList.remove('is-loading-panel');
  }

  private async handleFileChange(): Promise<void> {
    const file = this.fileInput.files?.[0];
    this.fileInput.value = '';
    if (!file) return;
    if (this.busy) return;
    this.busy = true;
    this.loadStatusEl.classList.remove('is-error');
    this.loadStatusEl.textContent = `Reading ${file.name}…`;
    try {
      const raw = await file.text();
      await this.callbacks.onLoadFile(raw);
      // If the caller hasn't disposed us by now (e.g. parse error),
      // clear busy so the player can try another file.
      this.busy = false;
    } catch (err) {
      this.busy = false;
      const message = err instanceof Error ? err.message : String(err);
      this.loadStatusEl.textContent = `Could not load: ${message}`;
      this.loadStatusEl.classList.add('is-error');
    }
  }

  private buildCard(opts: {
    title: string;
    tagline: string;
    blurb: string;
    icon: string;
    cta: string;
    onClick: () => void;
  }): HTMLElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'intro-card';
    card.innerHTML = `
      <div class="intro-card-icon">${opts.icon}</div>
      <div class="intro-card-tagline">${opts.tagline}</div>
      <div class="intro-card-title">${opts.title}</div>
      <p class="intro-card-blurb">${opts.blurb}</p>
      <div class="intro-card-cta">${opts.cta}</div>
    `;
    card.addEventListener('click', opts.onClick);
    return card;
  }
}

const NEW_GAME_ICON = `
<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M32 6l5.6 16.5L55 24l-13.5 11.4L46 53 32 43.5 18 53l4.5-17.6L9 24l17.4-1.5L32 6z" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round" />
</svg>`;

const LOAD_GAME_ICON = `
<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14 14h28l8 8v28a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V18a4 4 0 0 1 4-4z" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round" />
  <path d="M22 14v12h20V14" stroke="currentColor" stroke-width="2.2" />
  <path d="M22 36h20v18H22V36z" stroke="currentColor" stroke-width="2.2" />
  <path d="M28 41h8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
  <path d="M28 47h6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
</svg>`;

const SAVE_FILE_ICON = `
<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M7 6h14l4 4v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
  <path d="M10 6v6h10V6" stroke="currentColor" stroke-width="2" />
  <path d="M10 18h12v8H10z" stroke="currentColor" stroke-width="2" />
</svg>`;
